import { useEffect, useRef, useCallback } from "react";

/**
 * useBackHandler - Handle mobile back button for modals/overlays
 *
 * When a modal is open, pressing the phone's native back button will:
 * 1. Close the modal instead of navigating away
 * 2. Maintain proper browser history state
 *
 * This provides a native app-like experience on mobile devices.
 *
 * @param {boolean} isOpen - Whether the modal/overlay is open
 * @param {function} onClose - Function to close the modal/overlay
 * @param {string} [modalId] - Optional unique identifier for nested modals
 *
 * @example
 * function MyModal({ isOpen, onClose }) {
 *   useBackHandler(isOpen, onClose, "my-modal");
 *
 *   if (!isOpen) return null;
 *   return <div>Modal content</div>;
 * }
 */
function useBackHandler(isOpen, onClose, modalId = "modal") {
  // Track if we pushed a history state for this modal
  const hasPushedState = useRef(false);
  // Stable reference to onClose to avoid re-running effect
  const onCloseRef = useRef(onClose);

  // Update ref when onClose changes
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Memoized handler for popstate
  const handlePopState = useCallback(() => {
    // Only handle if we pushed a state and modal should be open
    if (hasPushedState.current) {
      hasPushedState.current = false;
      onCloseRef.current();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      // If modal just closed and we still have a pushed state, clean it up
      if (hasPushedState.current) {
        hasPushedState.current = false;
        // Don't call history.go(-1) here - it causes navigation issues
        // The state will just be orphaned in history which is fine
      }
      return;
    }

    // Only push state if we haven't already
    if (!hasPushedState.current) {
      // Push a history state when modal opens
      const stateKey = `modal-${modalId}-${Date.now()}`;
      window.history.pushState({ modalStateKey: stateKey }, "");
      hasPushedState.current = true;
    }

    // Listen for back button
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isOpen, modalId, handlePopState]);
}

export default useBackHandler;
